import type { Card, Deck } from './decks';
import { findCard } from './decks';
import type { Vote } from './rooms';

/**
 * Consolidating votes into a suggestion.
 *
 * This lives in `shared` rather than on either side alone: the server computes
 * it to attach to `RoomState`, and the client uses the same code to preview
 * results, so the two can never disagree about what the room decided.
 *
 * Non-committal cards (`?`, `☕`) are excluded throughout — see `decks.ts`.
 */

export interface CardTally {
  readonly card: Card;
  readonly count: number;
}

export interface VoteSummary {
  /** Votes that counted, i.e. excluding `?` and `☕`. */
  readonly countedVotes: number;
  /** Participants who picked a non-committal card. */
  readonly abstentions: number;
  /**
   * Arithmetic mean, or `null` when it would be meaningless: an ordinal deck
   * (t-shirt sizes) or no counted votes at all.
   */
  readonly mean: number | null;
  /** Middle value by rank. Valid on ordinal decks, unlike the mean. */
  readonly median: number | null;
  /** Most frequent cards. More than one entry means the room is split. */
  readonly modes: readonly Card[];
  /** The full distribution, in deck order, for rendering a histogram. */
  readonly tally: readonly CardTally[];
  /** True when every counted vote landed on the same card. */
  readonly isUnanimous: boolean;
  /**
   * Card the team should discuss first: the single mode when there is one,
   * otherwise the highest card voted, because the pessimist usually knows
   * something the rest of the room doesn't.
   */
  readonly suggestion: Card | null;
}

function median(sorted: readonly number[]): number | null {
  if (sorted.length === 0) return null;
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? null;
  const low = sorted[middle - 1];
  const high = sorted[middle];
  if (low === undefined || high === undefined) return null;
  return (low + high) / 2;
}

export function summarizeVotes(deck: Deck, votes: readonly Vote[]): VoteSummary {
  const counts = new Map<string, number>();
  for (const vote of votes) {
    counts.set(vote.cardId, (counts.get(vote.cardId) ?? 0) + 1);
  }

  // Deck order, not vote order, so the histogram reads low-to-high.
  const tally: CardTally[] = deck.cards
    .map((card) => ({ card, count: counts.get(card.id) ?? 0 }))
    .filter((entry) => entry.count > 0);

  const counted = tally.filter((entry) => entry.card.value !== null);
  const countedVotes = counted.reduce((total, entry) => total + entry.count, 0);
  const abstentions = votes.length - countedVotes;

  const values: number[] = [];
  for (const entry of counted) {
    for (let i = 0; i < entry.count; i += 1) values.push(entry.card.value as number);
  }
  values.sort((a, b) => a - b);

  const topCount = counted.reduce((max, entry) => Math.max(max, entry.count), 0);
  const modes = counted.filter((entry) => entry.count === topCount).map((entry) => entry.card);

  const highestVoted = counted.at(-1)?.card ?? null;
  const suggestion = modes.length === 1 ? (modes[0] ?? null) : highestVoted;

  return {
    countedVotes,
    abstentions,
    mean:
      deck.scale === 'numeric' && countedVotes > 0
        ? values.reduce((total, value) => total + value, 0) / countedVotes
        : null,
    median: median(values),
    modes,
    tally,
    isUnanimous: countedVotes > 0 && counted.length === 1,
    suggestion,
  };
}

/**
 * Renders a summary value back as a card label where possible. The mean of a
 * Fibonacci round is rarely a real card (8 and 13 average to 10.5), so the
 * nearest card is what the host actually gets offered. Exact ties resolve
 * downwards (10.5 on a Fibonacci deck yields 8, not 13) — the host can always
 * override, and quietly inflating an estimate is the worse default.
 */
export function nearestCard(deck: Deck, value: number): Card | null {
  let best: Card | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const card of deck.cards) {
    if (card.value === null) continue;
    const distance = Math.abs(card.value - value);
    if (distance < bestDistance) {
      best = card;
      bestDistance = distance;
    }
  }
  return best;
}

/** Convenience for the recap: the label a card id maps to, or the raw id. */
export function cardLabel(deck: Deck, cardId: string): string {
  return findCard(deck, cardId)?.label ?? cardId;
}
